import { Audience, ClinicalArea, Topic } from './metadata';
import { formatTopicLabel } from '../utils/metadataHelpers';
import type { OhpHospitalSite } from './hospitalEmailDomain';

export type { OhpHospitalSite };

export type UserProfileExtras = {
  namePrefix?: string;
  jobTitle?: string;
  profession?: string;
  organizationIds?: string[];
  clinicalAreas?: ClinicalArea[];
  topics?: Topic[];
};

export const NAME_PREFIX_OPTIONS = [
  { value: '', label: 'No prefix' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' },
  { value: 'Mx.', label: 'Mx.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
];

export const PROFESSION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Select a profession' },
  { value: Audience.Nurses, label: 'Nurse' },
  { value: Audience.Physicians, label: 'Physician' },
  { value: Audience.Transfusionists, label: 'Transfusionist' },
  { value: Audience.Laboratory, label: 'Technologists' },
  { value: Audience.Administrators, label: 'Administrator' },
  { value: Audience.QualitySafety, label: 'Quality and safety professional' },
  { value: Audience.Educators, label: 'Educator' },
  { value: Audience.StudentsTrainees, label: 'Student or trainee' },
  { value: Audience.DataAnalytics, label: 'Data or analytics professional' },
  { value: Audience.PolicyRegulators, label: 'Policy or regulator' },
  { value: Audience.ProcurementInventory, label: 'Procurement or inventory professional' },
  { value: Audience.PatientsCaregivers, label: 'Patient or caregiver' },
  { value: Audience.Other, label: 'Other' },
];

export const PROFILE_CLINICAL_AREAS: ClinicalArea[] = Object.values(ClinicalArea).filter(
  (area) => area !== ClinicalArea.Unknown
);

export const ALL_INTEREST_TOPICS: Topic[] = Object.values(Topic).filter(
  (topic) => topic !== Topic.Unknown
);

export const POPULAR_INTEREST_TOPICS: Topic[] = [
  Topic.IVIG,
  Topic.MHP,
  Topic.BloodSafety,
  Topic.Neonatal,
  Topic.Guidelines,
  Topic.Utilization,
  Topic.eLearning,
];

const TOPIC_LABEL_OVERRIDES: Partial<Record<Topic, string>> = {
  [Topic.IVIG]: 'IVIG',
  [Topic.SCIG]: 'SCIG',
  [Topic.MHP]: 'MHP',
  [Topic.RBC]: 'RBC',
  [Topic.ABPlasma]: 'AB plasma',
  [Topic.BloodSafety]: 'Blood safety',
  [Topic.IGOrderingOntario]: 'IG ordering in Ontario',
  [Topic.InventoryManagement]: 'Inventory management',
  [Topic.eLearning]: 'eLearning',
};

export function interestTopicLabel(topic: Topic): string {
  return TOPIC_LABEL_OVERRIDES[topic] ?? formatTopicLabel(topic);
}

function asString(record: Record<string, unknown>, key: string): string {
  return typeof record[key] === 'string' ? (record[key] as string) : '';
}

function asAllowedStrings<T extends string>(value: unknown, allowed: Set<T>): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is T => typeof item === 'string' && allowed.has(item as T));
}

export const WORK_EMAIL_ONBOARDING_DISMISSED_KEY = 'workEmailOnboardingDismissed';
export const ACCOUNT_SETUP_COMPLETED_KEY = 'accountSetupCompleted';

export function isAccountSetupCompleted(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== 'object') return false;
  return (metadata as Record<string, unknown>)[ACCOUNT_SETUP_COMPLETED_KEY] === true;
}

export function isWorkEmailOnboardingDismissed(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== 'object') return false;
  return (metadata as Record<string, unknown>)[WORK_EMAIL_ONBOARDING_DISMISSED_KEY] === true;
}

const CLINICAL_AREA_VALUES = new Set(Object.values(ClinicalArea));
const TOPIC_VALUES = new Set(Object.values(Topic));

export function emptyProfileExtras(): UserProfileExtras {
  return {
    namePrefix: '',
    jobTitle: '',
    profession: '',
    organizationIds: [],
    clinicalAreas: [],
    topics: [],
  };
}

export function readProfileExtras(metadata: unknown): UserProfileExtras {
  if (!metadata || typeof metadata !== 'object') {
    return emptyProfileExtras();
  }
  const record = metadata as Record<string, unknown>;
  const ids = record.organizationIds;
  const organizationIds = Array.isArray(ids)
    ? ids.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];

  return {
    namePrefix: asString(record, 'namePrefix'),
    jobTitle: asString(record, 'jobTitle'),
    profession: asString(record, 'profession'),
    organizationIds,
    clinicalAreas: asAllowedStrings(record.clinicalAreas, CLINICAL_AREA_VALUES),
    topics: asAllowedStrings(record.topics, TOPIC_VALUES),
  };
}

/** Clerk can require last_name even when signup only collects first name. */
export const CLERK_PLACEHOLDER_LAST_NAME = '-';

export function isPlaceholderLastName(value?: string | null): boolean {
  const trimmed = value?.replace(/\u00A0/g, ' ').trim() ?? '';
  return trimmed === '' || trimmed === CLERK_PLACEHOLDER_LAST_NAME || trimmed.toUpperCase() === 'NA';
}

export function formatUserDisplayName({
  prefix,
  firstName,
  lastName,
  email,
}: {
  prefix?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string {
  const last = isPlaceholderLastName(lastName) ? '' : lastName?.trim();
  const full = [prefix?.trim(), firstName?.trim(), last].filter(Boolean).join(' ');
  return full || email || 'Account';
}

export function hospitalsMatchingDomains(
  sites: readonly OhpHospitalSite[],
  emailDomains: readonly string[]
): OhpHospitalSite[] {
  const domainSet = new Set(emailDomains.map((domain) => domain.toLowerCase()));
  return sites.filter((site) => site.domains.some((domain) => domainSet.has(domain.toLowerCase())));
}
